import { useLocation, useNavigate } from 'react-router-dom';
import { useFilters } from '../contexts/FilterContext';
import { useAuth } from '../hooks/useAuth';
import { useCategories } from '../hooks/useCategories';
import { Button } from './ui';

const fixedFilters = [
  'All Events',
  'Free Events',
  'Today',
  'This Weekend',
];

interface SidebarProps {
  onFilterChange?: (filter: string) => void;
  activeFilter?: string;
}

export function Sidebar({ onFilterChange, activeFilter = 'All Events' }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const { categories, loading: categoriesLoading } = useCategories({ activeOnly: true });
  const { activeEventFilter, activeCategoryFilter, setActiveEventFilter, setActiveCategoryFilter } = useFilters();

  const isHomePage = location.pathname === '/';

  const handleEventFilterClick = (filter: string) => {
    // If not on homepage, navigate first
    if (!isHomePage) {
      navigate('/');
    }
    setActiveEventFilter(filter);
  };

  const handleCategoryFilterClick = (categoryId: string | null) => {
    // If not on homepage, navigate first
    if (!isHomePage) {
      navigate('/');
    }
    setActiveCategoryFilter(categoryId);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Only show on larger screens (hidden on mobile/tablet)
  return (
    <div className="hidden xl:flex xl:flex-col xl:w-80 bg-gray-50 border-r border-gray-200 h-full overflow-y-auto flex-shrink-0">
      <div className="p-6 flex flex-col h-full">
      {/* Create Event Button */}
      {user && (
        <Button
          onClick={() => navigate('/add-event')}
          className="w-full mb-6 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Event
        </Button>
      )}

      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">
          Discover
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/feed')}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              location.pathname === '/feed'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Activity Feed
          </button>
          <button
            onClick={() => navigate('/blogs')}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              location.pathname === '/blogs'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
            Event Blogs
          </button>
        </div>
      </div>

      {/* Event Filters */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
            Event Filters
          </h3>
          {!isHomePage && (
            <span className="text-xs text-gray-500 italic">
              (Go to Home to filter)
            </span>
          )}
        </div>

        {/* Fixed Filters */}
        <div className="space-y-1 mb-4">
          {fixedFilters.map((filter) => (
            <button
              key={filter}
              onClick={() => handleEventFilterClick(filter)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isHomePage && activeEventFilter === filter
                  ? 'bg-primary-100 text-primary-700'
                  : isHomePage
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              title={!isHomePage ? 'Click to go to Home and apply this filter' : ''}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Categories Separator */}
        {categories.length > 0 && (
          <>
            <div className="border-t border-gray-200 my-4"></div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">
              Categories
            </h4>
          </>
        )}

        {/* Category Filters */}
        <div className="space-y-1">
          {categoriesLoading && categories.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Loading categories...
            </div>
          )}
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                if (!category.id) return;
                handleCategoryFilterClick(activeCategoryFilter === category.id ? null : category.id);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                isHomePage && activeCategoryFilter === category.id
                  ? 'bg-primary-100 text-primary-700'
                  : isHomePage
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              title={!isHomePage ? 'Click to go to Home and apply this filter' : ''}
            >
              {category.icon && <span>{category.icon}</span>}
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>
      

      {/* Quick Actions */}
      {user && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">
            Quick Actions
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/profile')}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Profile
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Terms & Conditions
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Privacy Policy
            </button>
          </div>
        </div>
      )}

      {/* Admin Section */}
      {isAdmin && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Admin
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/admin/blog')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                location.pathname.startsWith('/admin/blog')
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Manage Blogs
            </button>
            <button
              onClick={() => navigate('/admin/moderation')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                location.pathname === '/admin/moderation'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Moderation
            </button>
          </div>
        </div>
      )}

      {/* User Info & Sign Out */}
      {user && (
        <div className="mt-auto pt-6 border-t border-gray-200">
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-900">
              {user.displayName || user.email}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user.email}
            </p>
          </div>
          
          <button
            onClick={handleSignOut}
            className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-pink-600 hover:text-pink-700 hover:bg-pink-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      )}

        {/* Sign In prompt for non-authenticated users */}
        {!user && (
          <div className="mt-auto pt-6 border-t border-gray-200">
            <div className="bg-primary-50 rounded-xl p-4 text-center">
              <p className="text-sm text-primary-700 mb-3">
                Sign in to create events and manage your profile
              </p>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                size="sm"
                className="w-full"
              >
                Sign In
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}